import React, { useCallback, useEffect, useMemo, useState } from 'react';
import authAxios from '../utils/authAxios';
import PidMethodModal from '../components/PidMethodModal';
import PidTableShell, { CommodityChips } from '../components/PidTableShell';
import { PageWrapper, ContentWrapper } from '../styles/PackageIndexStyles';
import StatusPicker from '../components/StatusPicker';
import { PID_STATUSES, pidStatusColor, normalizePidStatus, pidStatusRank } from '../utils/pidStatus';
import { changePidStatus } from '../utils/pidStatusApi';

// Group flat PID rows into Method -> Commodity -> Style -> [size options].
// The table shows one row per method; the full structure feeds the edit panel.
const groupRowsHierarchically = (data) => {
  const grouped = {};
  data.forEach(row => {
    const method = row.MethodName;
    const commodity = row.Commodity;
    const style = row.Style;
    const sizeOption = row.SizeOption || row.SizeOptionUniqueID?.toString();

    if (!grouped[method]) grouped[method] = { info: row, commodities: {} };
    if (!grouped[method].commodities[commodity]) grouped[method].commodities[commodity] = {};
    if (!grouped[method].commodities[commodity][style]) grouped[method].commodities[commodity][style] = [];

    if (!grouped[method].commodities[commodity][style].includes(sizeOption)) {
      grouped[method].commodities[commodity][style].push(sizeOption);
    }
  });

  return grouped;
};

const PidMethodsPage = () => {
  const [loading, setLoading] = useState(true);
  const [pidData, setPidData] = useState([]);
  const [dropdowns, setDropdowns] = useState({ methods: [], commodities: [], styles: [], sizeOptions: [] });
  const [showPanel, setShowPanel] = useState(false);
  const [editGroup, setEditGroup] = useState(null);

  useEffect(() => { fetchAllData(); }, []);

  const fetchAllData = async () => {
    setLoading(true);
    const [methodsRes, commoditiesRes, stylesRes, sizesRes, pidMethodsRes] = await Promise.all([
      authAxios.get('/api/methods'),
      authAxios.get('/api/commodities'),
      authAxios.get('/api/packtypes'),
      authAxios.get('/api/sizeoptions/dropdown?origin=Domestic'),
      authAxios.get('/api/pidmethods')
    ]);

    const usedMethodIDs = pidMethodsRes.data.map(row => row.MethodUniqueID);
    const filteredMethods = methodsRes.data
      .filter(m => !usedMethodIDs.includes(m.UniqueID))
      .map(m => ({ value: m.UniqueID, label: m.MethodName }));

    const commodities = commoditiesRes.data.map(c => ({ value: c.UniqueID, label: c.Commodity }));
    const styles = stylesRes.data.map(s => ({ value: s.UniqueID, label: s.Style }));
    const sizeOptions = sizesRes.data.map(s => ({
      value: s.UniqueID,
      label: s.SizeOption,
      CommodityUniqueID: Number(s.CommodityUniqueID)
    }));

    setDropdowns({ methods: filteredMethods, commodities, styles, sizeOptions });
    setPidData(pidMethodsRes.data);
    setLoading(false);
  };

  const handleAddEntrySubmit = async (rows) => {
    for (const row of rows) {
      await authAxios.post('/api/pidmethods', row);
    }
    fetchAllData();
  };

  const handleStatus = useCallback(async (methodUniqueId, next) => {
    const prevById = {};
    setPidData(rows => rows.map(r => {
      if (r.MethodUniqueID === methodUniqueId) { prevById[r.UniqueID] = r.Status; return { ...r, Status: next }; }
      return r;
    }));
    try {
      await changePidStatus('methods', methodUniqueId, next); // Methods never block
    } catch (e) {
      console.error('[PidMethods] status change failed', e);
      setPidData(rows => rows.map(r => (r.MethodUniqueID === methodUniqueId ? { ...r, Status: prevById[r.UniqueID] ?? r.Status } : r)));
      alert(e?.response?.data?.error || 'Failed to update status.');
    }
  }, []);

  const groups = useMemo(() => {
    const grouped = groupRowsHierarchically(pidData);
    return Object.entries(grouped).map(([method, methodGroup]) => ({
      method,
      baseInfo: methodGroup.info,
      commodities: methodGroup.commodities,
      commodityNames: Object.keys(methodGroup.commodities),
    }));
  }, [pidData]);

  const columns = useMemo(() => [
    {
      key: 'Status', label: 'Status', interactive: true, cardRole: 'status',
      sortValue: (g) => pidStatusRank(g.baseInfo?.Status),
      render: (g) => (
        <StatusPicker
          status={g.baseInfo?.Status}
          confirm
          size="sm"
          statuses={PID_STATUSES}
          colorFor={pidStatusColor}
          normalize={normalizePidStatus}
          ariaLabel={`Status for method ${g.method || ''}`}
          onChange={(next) => handleStatus(g.baseInfo.MethodUniqueID, next)}
        />
      ),
    },
    { key: 'Type', label: 'Type', sortValue: (g) => g.baseInfo?.Type || '', render: (g) => g.baseInfo?.Type },
    { key: 'Retailer', label: 'Retailer', sortValue: (g) => g.baseInfo?.Retailer || '', render: (g) => g.baseInfo?.Retailer },
    { key: 'MethodName', label: 'Method Name', emphasis: true, cardRole: 'title', sortValue: (g) => g.method || '', render: (g) => g.method },
    {
      key: 'Commodity', label: 'Commodity',
      sortValue: (g) => g.commodityNames[0] || '',
      render: (g) => <CommodityChips names={g.commodityNames} />,
    },
  ], [handleStatus]);

  const searchText = useCallback((g) => [
    g.baseInfo?.Status, g.baseInfo?.Type, g.baseInfo?.Retailer, g.method, ...g.commodityNames,
  ].filter(Boolean).join(' '), []);

  return (
    <PageWrapper>
      <ContentWrapper>
        <PidTableShell
          title="PID Methods"
          columns={columns}
          rows={groups}
          rowKey={(g) => g.baseInfo?.MethodUniqueID ?? g.method}
          searchText={searchText}
          defaultSort={{ key: 'MethodName', dir: 'asc' }}
          onAdd={() => setShowPanel(true)}
          onRowClick={(g) => setEditGroup({ method: g.method, commodities: g.commodities, baseInfo: g.baseInfo })}
          rowAriaLabel={(g) => `Edit method ${g.method || ''}`}
          loading={loading}
          loadingText="Loading PID Methods..."
          emptyText="No PID Methods found."
        />

        {showPanel && (
          <PidMethodModal
            mode="create"
            dropdowns={dropdowns}
            onClose={() => setShowPanel(false)}
            onCreate={async (rows) => {
              await handleAddEntrySubmit(rows);
              setShowPanel(false);
            }}
          />
        )}

        {editGroup && (
          <PidMethodModal
            mode="edit"
            groupData={editGroup}
            dropdowns={dropdowns}
            onClose={() => setEditGroup(null)}
            onSaveEdit={async (updatedRows) => {
              const groupStatus = editGroup?.baseInfo?.Status;
              await authAxios.delete(`/api/pidmethods/group/by-id/${updatedRows[0].MethodUniqueID}`);
              for (const row of updatedRows) {
                await authAxios.post('/api/pidmethods', groupStatus ? { ...row, Status: groupStatus } : row);
              }
              setEditGroup(null);
              fetchAllData();
            }}
            onDeleteGroup={async () => {
              await authAxios.delete(`/api/pidmethods/group/by-id/${editGroup.baseInfo.MethodUniqueID}`);
              setEditGroup(null);
              fetchAllData();
            }}
          />
        )}
      </ContentWrapper>
    </PageWrapper>
  );
};

export default PidMethodsPage;
